/**
 * ProviderSelector — shared control for attributing an action (file upload,
 * schema creation) to one of the current user's authorized providers.
 *
 * The set of *authorized* provider IDs always comes from
 * `useCurrentUser().providerIds` (backed by kodex_user_provider_access via
 * GET /api/v1/me) — the same source the backend uses in
 * `resolve_active_provider` to decide which provider_id can be stamped on a
 * write. Display names for those IDs are resolved via the session-cached
 * `fetchProvidersCached` client rather than a new fetch path.
 *
 * Behaviour is driven entirely by how many *eligible* providers remain after
 * intersecting the user's authorized IDs with `allowedStatus` (when given):
 *   - 0 eligible providers  -> disabled, explanatory message only.
 *   - 1 eligible provider   -> locked, read-only field — nothing to choose,
 *                              so we don't ask the user to make a redundant
 *                              selection (and we auto-select it for them).
 *   - 2+ eligible providers -> a real Select the user must expand and pick from.
 *
 * Parents that need a reminder pill elsewhere (e.g. Assign schemas) should
 * persist the display name from `onChange` meta and render `LockedProviderField`
 * — do not mount a second ProviderSelector just to resolve a label.
 */

import { useEffect, useState } from "react";
import { SelectField, Spinner, AlertMessage } from "@/components/reusable";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fetchProvidersCached } from "@/services/providersApi";
import type { ProviderOut, ProviderStatus } from "@/types/providers";
import { cn } from "@/lib/utils";

/** Optional display metadata passed alongside the selected provider id. */
export type ProviderSelectionMeta = { name: string };

export interface ProviderSelectorProps {
  /** Currently selected provider ID, or null when none is selected yet. */
  value: string | null;
  /**
   * Called with the newly selected provider ID (or the auto-selected single one).
   * Optional `meta.name` lets parents persist a display label without re-fetching.
   */
  onChange: (providerId: string | null, meta?: ProviderSelectionMeta) => void;
  /**
   * Restrict eligible providers to this status (e.g. "active" for Upload).
   * Omit to allow a provider in any status (e.g. Schema Builder, where
   * ownership can be set while a provider is still pending onboarding).
   */
  allowedStatus?: ProviderStatus;
  /** Overrides the default "no eligible provider" message. */
  emptyMessage?: string;
  className?: string;
}

/** Grey locked chip for single-provider lock and parent-owned reminder pills. */
export function LockedProviderField({ label, className }: { label: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-4 py-3 text-sm text-[var(--color-text-primary)]",
        className
      )}
      aria-readonly="true"
      data-testid="provider-selector-locked"
    >
      {label}
    </div>
  );
}

/**
 * Resolves the current user's authorized provider IDs into full provider
 * records, filtered by `allowedStatus` when given. Kept separate from the
 * component so the fetch/loading/error state is easy to reason about on its
 * own, independent of rendering.
 */
function useEligibleProviders(
  authorizedIds: string[] | undefined,
  allowedStatus?: ProviderStatus
): { providers: ProviderOut[]; loading: boolean; error: string | null } {
  const [providers, setProviders] = useState<ProviderOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // authorizedIds is undefined while identity (GET /me) is still resolving --
    // stay in the loading state while we wait for the identity to resolve.
    if (authorizedIds === undefined) {
      setLoading(true);
      return;
    }

    // Confirmed zero authorized providers -- nothing to fetch, and no point
    // showing a loading spinner for a request we're not going to make.
    if (authorizedIds.length === 0) {
      setProviders([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchProvidersCached()
      .then((res) => {
        if (cancelled) return;
        const authorizedSet = new Set(authorizedIds);
        setProviders(
          res.items.filter(
            (p) =>
              authorizedSet.has(p.provider_id) &&
              (allowedStatus === undefined || p.provider_status === allowedStatus)
          )
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load providers.");
        setProviders([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // authorizedIds is a fresh array on every /me resolution; join() keeps the
    // effect from re-firing on every parent re-render when its contents are
    // unchanged, while still distinguishing "unresolved" (undefined) from
    // "resolved to zero" ("") since only the latter produces an empty string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedIds?.join(","), allowedStatus]);

  return { providers, loading, error };
}

export function ProviderSelector({
  value,
  onChange,
  allowedStatus,
  emptyMessage,
  className,
}: ProviderSelectorProps) {
  const currentUser = useCurrentUser();
  const authorizedIds = currentUser?.providerIds;
  const { providers, loading, error } = useEligibleProviders(authorizedIds, allowedStatus);

  // Auto-select the only eligible provider so the user isn't asked to
  // confirm a choice that was never really a choice.
  useEffect(() => {
    if (loading || providers.length !== 1) return;
    const [onlyProvider] = providers;
    if (onlyProvider && value !== onlyProvider.provider_id) {
      onChange(onlyProvider.provider_id, { name: onlyProvider.provider_name });
    }
  }, [loading, providers, value, onChange]);

  if (loading) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 text-sm text-[var(--color-text-tertiary)]",
          className
        )}
      >
        <Spinner size="sm" />
        Loading providers…
      </div>
    );
  }

  if (error) {
    return <AlertMessage variant="error" message={error} className={className} />;
  }

  if (providers.length === 0) {
    const defaultMessage =
      (authorizedIds?.length ?? 0) === 0
        ? "No provider is assigned to your account. Contact an admin to get access."
        : "None of your assigned providers are currently eligible.";
    return (
      <AlertMessage
        variant="warning"
        message={emptyMessage ?? defaultMessage}
        className={className}
      />
    );
  }

  if (providers.length === 1) {
    const [onlyProvider] = providers;
    return <LockedProviderField label={onlyProvider?.provider_name ?? ""} className={className} />;
  }

  return (
    <SelectField
      value={value ?? ""}
      onValueChange={(nextId) => {
        if (!nextId) {
          onChange(null);
          return;
        }
        const matched = providers.find((p) => p.provider_id === nextId);
        onChange(nextId, matched ? { name: matched.provider_name } : undefined);
      }}
      options={providers.map((p) => ({ value: p.provider_id, label: p.provider_name }))}
      placeholder="Select a provider first"
      className={className}
    />
  );
}
